import { Controller } from "@hotwired/stimulus";
import SignaturePad from "signature_pad";
import consumer from "../../channels/consumer";

// Used in two contexts:
//   - record_stat_dec page (NOK): observe=false → broadcasts signature strokes and live
//     video (via WebRTC) to the admin page.
//   - admin statutory_declarations show page (JP): observe=true → renders incoming
//     strokes on a read-only canvas and the live camera feed in a <video> element.

export default class extends Controller {
  static targets = ["canvas", "placeholder", "video"];
  static values = { statDecId: String, observe: Boolean, iceServersUrl: String, fallbackIceServers: Array };

  connect() {
    this.pendingCandidates = [];
    this.setupChannel();

    if (this.observeValue) {
      this.setupObserverCanvas();
    } else {
      this.setupBroadcaster();
    }
  }

  disconnect() {
    this.subscription?.unsubscribe();

    if (this.strokeHandler) {
      document.removeEventListener("pointerup", this.strokeHandler);
    }

    if (this.clearHandler) {
      document.removeEventListener("signature_pad:cleared", this.clearHandler);
    }

    if (this.boundResizeCanvas) {
      window.removeEventListener("resize", this.boundResizeCanvas);
    }

    if (this.streamPollInterval) {
      clearInterval(this.streamPollInterval);
    }

    this.peerConnection?.close();
  }

  // --- Observer (JP / admin) ---

  setupObserverCanvas() {
    this.boundResizeCanvas = this.resizeCanvas.bind(this);
    window.addEventListener("resize", this.boundResizeCanvas);
    this.resizeCanvas();

    this.sigPad = new SignaturePad(this.canvasTarget, { minWidth: 1, maxWidth: 3 });
    this.sigPad.off();
  }

  resizeCanvas() {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const canvas = this.canvasTarget;

    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = 250 * ratio;
    canvas.style.height = "250px";
    canvas.getContext("2d").scale(ratio, ratio);

    if (this.sigPad) this.sigPad.clear();
  }

  renderReceivedStrokes(data) {
    if (!data.strokes) return;

    const canvas = this.canvasTarget;
    const scaleX = data.canvasWidth ? canvas.offsetWidth / data.canvasWidth : 1;
    const scaleY = data.canvasHeight ? canvas.offsetHeight / data.canvasHeight : 1;
    const scaledStrokes = data.strokes.map((group) => ({
      ...group,
      points: group.points.map((p) => ({ ...p, x: p.x * scaleX, y: p.y * scaleY })),
    }));

    this.sigPad.fromData(scaledStrokes);

    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.toggle("hidden", data.strokes.length > 0);
    }
  }

  async handleObserverOffer(data) {
    // Don't tear down a connection that's already working.
    if (this.peerConnection?.connectionState === "connected") return;

    this.peerConnection?.close();
    this.pendingCandidates = [];

    const iceServers = await this.fetchIceServers();
    this.peerConnection = new RTCPeerConnection({ iceServers });

    this.peerConnection.onicecandidate = ({ candidate }) => {
      if (candidate) {
        this.subscription.send({ type: "ice_candidate", candidate: candidate.toJSON() });
      }
    };

    this.peerConnection.ontrack = ({ streams: [stream] }) => {
      if (this.hasVideoTarget) {
        this.videoTarget.srcObject = stream;
        this.videoTarget.play().catch(() => { /* noop */ });
      }
    };

    await this.peerConnection.setRemoteDescription(new RTCSessionDescription(data.sdp));

    for (const candidate of this.pendingCandidates) {
      await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => { /* noop */ });
    }
    this.pendingCandidates = [];

    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);
    this.subscription.send({ type: "answer", sdp: this.peerConnection.localDescription });
  }

  handleObserverIceCandidate(candidate) {
    if (!candidate) return;

    if (this.peerConnection?.remoteDescription) {
      this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => { /* noop */ });
    } else {
      this.pendingCandidates.push(candidate);
    }
  }

  // --- Broadcaster (NOK / customer signing page) ---

  setupBroadcaster() {
    this.strokeHandler = () => {
      const sigPadEl = document.querySelector("[data-controller='signature_pad']");
      if (!sigPadEl) return;

      const sigPadController = this.application.getControllerForElementAndIdentifier(
        sigPadEl,
        "signature_pad",
      );
      if (!sigPadController?.sigPad) return;

      const canvas = sigPadController.sigPad.canvas;
      this.subscription.send({
        strokes: sigPadController.sigPad.toData(),
        canvasWidth: canvas.offsetWidth,
        canvasHeight: canvas.offsetHeight,
      });
    };

    document.addEventListener("pointerup", this.strokeHandler);

    this.clearHandler = () => {
      this.subscription.send({ strokes: [], canvasWidth: 0, canvasHeight: 0 });
    };
    document.addEventListener("signature_pad:cleared", this.clearHandler);

    // Poll until the statutory_declaration controller has acquired a camera stream.
    this.streamPollInterval = setInterval(() => {
      const statDecCtrl = this.application.getControllerForElementAndIdentifier(
        this.element,
        "statutory_declaration",
      );
      if (!statDecCtrl?.cameraStream) return;

      clearInterval(this.streamPollInterval);
      this.streamPollInterval = null;
      this.cameraStream = statDecCtrl.cameraStream;
      this.sendOffer();
    }, 500);
  }

  async sendOffer() {
    if (!this.cameraStream) return;

    this.peerConnection?.close();
    const iceServers = await this.fetchIceServers();
    this.peerConnection = new RTCPeerConnection({ iceServers });

    this.cameraStream.getTracks().forEach((track) => {
      this.peerConnection.addTrack(track, this.cameraStream);
    });

    this.peerConnection.onicecandidate = ({ candidate }) => {
      if (candidate) {
        this.subscription.send({ type: "ice_candidate", candidate: candidate.toJSON() });
      }
    };

    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    this.subscription.send({ type: "offer", sdp: this.peerConnection.localDescription });
  }

  handleRequestOffer() {
    // If the stream is already available, re-send a fresh offer for late-joining observers.
    if (this.cameraStream) this.sendOffer();
    // Otherwise the streamPollInterval will call sendOffer when the stream becomes available.
  }

  async handleBroadcasterAnswer(data) {
    if (!this.peerConnection || !data.sdp) return;
    await this.peerConnection.setRemoteDescription(new RTCSessionDescription(data.sdp)).catch(() => { /* noop */ });
  }

  handleBroadcasterIceCandidate(candidate) {
    if (!candidate || !this.peerConnection) return;
    this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => { /* noop */ });
  }

  async fetchIceServers() {
    if (!this.iceServersUrlValue) return this.fallbackIceServersValue;

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;
      const response = await fetch(this.iceServersUrlValue, {
        headers: { "X-CSRF-Token": csrfToken, Accept: "application/json" },
      });
      if (!response.ok) return this.fallbackIceServersValue;
      const data = await response.json();
      return data.ice_servers?.length ? data.ice_servers : this.fallbackIceServersValue;
    } catch {
      return this.fallbackIceServersValue;
    }
  }

  // --- Shared: Action Cable subscription ---

  setupChannel() {
    this.subscription = consumer.subscriptions.create(
      { channel: "StatDecSigningChannel", stat_dec_id: this.statDecIdValue },
      {
        connected: () => {
          // Ask the broadcaster to re-send its offer so a late-joining JP gets the stream.
          if (this.observeValue) this.subscription.send({ type: "request_offer" });
        },
        received: (data) => {
          if (this.observeValue) {
            if (data.strokes !== undefined) this.renderReceivedStrokes(data);
            if (data.type === "offer") this.handleObserverOffer(data);
            if (data.type === "ice_candidate") this.handleObserverIceCandidate(data.candidate);
            if (data.type === "reload") window.location.reload();
          } else {
            if (data.type === "request_offer") this.handleRequestOffer();
            if (data.type === "answer") this.handleBroadcasterAnswer(data);
            if (data.type === "ice_candidate") this.handleBroadcasterIceCandidate(data.candidate);
          }
        },
      },
    );
  }
}
